import React from 'react';

import {
  Autocomplete,
  TextField,
  InputLabel as BaseInputLabel,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import update from 'immutability-helper';
import _ from 'lodash';
import {
  IExtendedEnum,
  IGenericEnum
} from 'modules/layer2/enumCache'; // TODO: Move this file to layer 3
import {
  Controller,
  FieldValues
} from 'react-hook-form';

import {
  IBaseEnumMultiSelectFieldProps,
  IBaseGroupedEnumMultiSelectFieldProps,
  IEnumFieldControllerProps,
  IEnumMultiSelectFieldProps,
  IGroupedEnumMultiSelectFieldProps,
  IGroupedOption,
  IOption,
  IUseGetEnumValuesTuple
} from '../shared/enumSelectFieldTypes';
import * as SelectOptionGroup from '../shared/SelectOptionGroup';
import * as SelectOptionItem from '../shared/SelectOptionItem';



const InputLabel = styled(BaseInputLabel, {
  shouldForwardProp: prop => prop !== 'isDisabled'
})<{isDisabled?: boolean}>(({ isDisabled }) => ({
  color: '#486C82',
  opacity: isDisabled ? 0.38 : 1,
  fontSize: 12
}));



export const BaseEnumMultiSelectField: React.FC<IBaseEnumMultiSelectFieldProps> = props => {
  const options = React.useMemo(
    (): IOption[] => props.options,
    [props.options]
  );

  const optionsById = React.useMemo(
    () => _.keyBy(options, option => option.id),
    [options]
  );

  const selectedOptions = React.useMemo(
    () =>
      !props.optionIds ?
        [] :
        _.map(props.optionIds, id => optionsById[id]),
    [
      props.optionIds,
      optionsById
    ]
  );



  function handleChange(values: IOption[]) {
    props.onChangeOptionIds(_.map(values, v => v.id));
  }



  return (
    <Box>
      <InputLabel isDisabled={props.isDisabled}>
        {props.label}
      </InputLabel>

      <Autocomplete
        options={options}
        value={selectedOptions}
        onChange={(ev, values) => handleChange(values)}
        getOptionLabel={option => option.label}
        multiple
        disabled={props.isDisabled}
        disableCloseOnSelect
        renderOption={(props, option, state) => (
          <SelectOptionItem.FC
            {...props}
            variant="checkbox"
            key={option.id}
            text={option.label}
            selected={state.selected}
          />
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={_.isEmpty(selectedOptions) ? props.placeholder : undefined}
            variant="outlined"
            error={!!props.errorMessage}
            helperText={props.errorMessage} />
        )} />
    </Box>
  );
};



export function genEnumMultiSelectFieldComponent<TEnumName extends string, TEnum extends IGenericEnum<TEnumName>>(
  useGetEnumValues: () => IUseGetEnumValuesTuple<TEnumName, TEnum>,
  selectFieldOptions?: {
    label?: React.ReactNode;
  }
) {
  const EnumSelectField: React.FC<IEnumMultiSelectFieldProps<TEnumName>> = props => {
    const tuple = useGetEnumValues();

    const options = React.useMemo(
      () =>
        tuple.loading ?
          [] :
          _.map(tuple.data.enumValues, (enumValue): IOption => ({
            id: enumValue.name,
            label: enumValue.displayName
          })),
      [
        tuple.loading,
        tuple.data.enumValues
      ]
    );



    return (
      <BaseEnumMultiSelectField
        {...props}
        label={selectFieldOptions?.label}
        options={options}
        placeholder={props.placeholder}
        optionIds={props.optionIds}
        onChangeOptionIds={optionIds => props.onChangeOptionIds(optionIds)}
        isDisabled={props.isDisabled || tuple.loading} />
    );
  };

  return EnumSelectField;
}



export function genEnumMultiSelectFieldControllerComponent<TFormState extends FieldValues, TOptionName extends string>(
  Component: React.FC<IEnumMultiSelectFieldProps<TOptionName>>
) {
  const EnumSelectFieldController: React.FC<IEnumFieldControllerProps<TFormState, TOptionName>> = props => {
    return (
      <Controller
        name={props.fieldKey}
        control={props.formControl}
        rules={{ required: props.isRequired }}
        render={({ field, fieldState }) => (
          <Component
            label={props.label}
            optionIds={field.value}
            placeholder={props.placeholder}
            onChangeOptionIds={field.onChange}
            errorMessage={fieldState.error?.message}
            isDisabled={props.isDisabled} />
        )} />
    );
  };

  return EnumSelectFieldController;
}



export const BaseGroupedEnumMultiSelectField = <T extends string>(props: IBaseGroupedEnumMultiSelectFieldProps<T>) => {
  const [optionName2IsOpen, setOptionId2IsOpen] = React.useState<_.Dictionary<boolean>>({});

  const optionName2IsSelected = React.useMemo(() =>
    _.mapValues(optionName2IsOpen, (isOpen, id) => _.includes(props.optionIds, id)),
  [optionName2IsOpen, props.optionIds]);

  const selectedOptions = React.useMemo(() =>
    _.filter(props.options, option => optionName2IsSelected[option.id]),
  [optionName2IsSelected, props.options]);



  // Initialize whether groups are open or closed when props change.
  React.useEffect(() => {
    setOptionId2IsOpen(old => ({
      ..._.mapValues(_.keyBy(props.options, opt => opt.id), () => false),
      // Overwrite props with values that exist in props and are marked open
      ..._.pickBy(old, (isOpen, id) => isOpen && !_.isNil((_.find(props.options, o => o.id === id))))
    }));
  }, [props.options]);



  const optionName2ParentName = React.useMemo(() =>
    _.mapValues(_.keyBy(props.options, option => option.name), v => v.parentName),
  [props.options]);



  function handleOpenGroup(id: T) {
    if (!optionName2IsOpen[id]) {
      setOptionId2IsOpen(
        update(optionName2IsOpen, {
          [id]: { $set: true }
        })
      );

    } else {
      setOptionId2IsOpen(
        update(optionName2IsOpen, {
          [id]: { $set: false },
          ..._.reduce(getChildren(id), (acc, id) => ({
            ...acc,
            [id]: { $set: false }
          }), {})
        })
      );
    }
  }



  function handleCheckOption(name: T) {
    const childrenNames = getChildren(name);
    const result = update(optionName2IsSelected, {
      [name]: { $set: !optionName2IsSelected[name] },
      ..._.mapValues(_.keyBy(childrenNames, childName => childName), () => ({
        // This is intentionally `name`. We want to follow the parents selection state.
        $set: !optionName2IsSelected[name]
      }))
    });

    props.onChangeOptionIds(_.keys(_.pickBy(result, isSelected => isSelected)));
  }



  function getChildren(parentName: T) {
    const parentName2ChildrenNames = _.chain(props.options)
      .filter(opt => !_.isNil(opt.parentName))
      .groupBy(o => o.parentName ?? '')
      .mapValues(opts => _.map(opts, o => o.name))
      .value();

    function recursivelyGetChildren(p: T, childrenNames: T[]): T[] {
      const children = parentName2ChildrenNames[p];

      if (_.isEmpty(children)) {
        return childrenNames;
      }

      return _.flatMap(children, child => recursivelyGetChildren(child, [...childrenNames, child]));
    }

    return _.uniq(recursivelyGetChildren(parentName, []));
  }



  function handleChange(values: IGroupedOption<T>[]) {
    props.onChangeOptionIds(_.map(values, v => v.id));
  }



  return (
    <Box>
      <InputLabel isDisabled={props.isDisabled}>
        {props.label}
      </InputLabel>

      <Autocomplete
        options={props.options}
        value={selectedOptions}
        onChange={(ev, values) => handleChange(values)}
        multiple
        disableCloseOnSelect
        disabled={props.isDisabled}
        renderOption={(props, option, state) => {
          if (
            option.parentName === null || // Top level options
            !!optionName2IsOpen[option.parentName] // group member options
          ) {
            const indentLevel = __getIndentLevel(option.name, optionName2ParentName, 0);
            return option.isLeaf ? (
              <SelectOptionItem.FC
                {...props}
                variant="checkbox"
                key={option.id}
                text={option.label}
                indentLevel={indentLevel}
                selected={state.selected}
                overrideOnChange={() => handleCheckOption(option.name)} />

            ) : (
              <SelectOptionGroup.FC
                {...props}
                key={option.id}
                label={option.label}
                indentLevel={indentLevel}
                isOpen={optionName2IsOpen[option.id]}
                onOpen={() => handleOpenGroup(option.name)}
                onCheck={() => handleCheckOption(option.name)}
                checkState={optionName2IsSelected[option.id] ? 'checked' : 'unchecked'} />
            );
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={_.isEmpty(props.optionIds ?? []) ? props.placeholder : undefined}
            error={!!props.errorMessage}
            helperText={props.errorMessage} />
        )} />
    </Box>
  );
};



export function genGroupedEnumMultiSelectFieldComponent<TEnumName extends string, TEnum extends IExtendedEnum<TEnumName>>(
  useGetEnumValues: () => IUseGetEnumValuesTuple<TEnumName, TEnum>,
  selectFieldOptions?: {
    label?: React.ReactNode;
  }
) {
  const EnumSelectField: React.FC<IGroupedEnumMultiSelectFieldProps<TEnumName>> = props => {
    const tuple = useGetEnumValues();

    const options = React.useMemo(() => {
      if (tuple.loading) {
        return [];
      }

      const parentNames = new Set(_.compact(_.map(tuple.data.enumValues, val => val.parentName)));

      return _.map(tuple.data.enumValues, (enumValue): IGroupedOption<TEnumName> => ({
        id: enumValue.name,
        label: enumValue.displayName,
        displayName: enumValue.displayName,
        name: enumValue.name,
        parentName: enumValue.parentName,
        ranking: enumValue.ranking,
        isLeaf: !parentNames.has(enumValue.name)
      }));
    }, [
      tuple.loading,
      tuple.data.enumValues
    ]);



    return (
      <BaseGroupedEnumMultiSelectField
        {...props}
        label={selectFieldOptions?.label}
        options={options}
        optionIds={props.optionIds}
        placeholder={props.placeholder}
        onChangeOptionIds={optionId => props.onChangeOptionIds(optionId)}
        isDisabled={props.isDisabled || tuple.loading} />
    );
  };

  return EnumSelectField;
}



export function genGroupedEnumMultiSelectFieldControllerComponent<TFormState extends FieldValues, TOptionName extends string>(
  Component: React.FC<IGroupedEnumMultiSelectFieldProps<TOptionName>>
) {
  const EnumSelectFieldController: React.FC<IEnumFieldControllerProps<TFormState, TOptionName>> = props => {
    return (
      <Controller
        name={props.fieldKey}
        control={props.formControl}
        rules={{ required: props.isRequired }}
        render={({ field, fieldState }) => (
          <Component
            label={props.label}
            optionIds={field.value}
            placeholder={props.placeholder}
            onChangeOptionIds={field.onChange}
            errorMessage={fieldState.error?.message}
            isDisabled={props.isDisabled} />
        )} />
    );
  };

  return EnumSelectFieldController;
}



export function genBasicGroupedMultiSelectFieldControllerComponent<TFormState extends FieldValues, TEnum extends string>(
  options: IGroupedOption<TEnum>[]
) {
  const EnumSelectFieldController: React.FC<IEnumFieldControllerProps<TFormState, typeof options[number]['id']>> = props => {
    return (
      <Controller
        name={props.fieldKey}
        control={props.formControl}
        rules={{ required: props.isRequired }}
        render={({ field, fieldState }) => (
          <BaseGroupedEnumMultiSelectField
            label={props.label}
            optionIds={field.value}
            options={options}
            onChangeOptionIds={field.onChange}
            errorMessage={fieldState.error?.message}
            isDisabled={props.isDisabled} />
        )} />
    );
  };

  return EnumSelectFieldController;
}



function __getIndentLevel<TEnum extends string>(optionName: TEnum, optionName2ParentName: _.Dictionary<TEnum | null>, indentLevel: number): number {
  const parentName = optionName2ParentName[optionName];
  return !parentName
    ? indentLevel
    : __getIndentLevel(parentName, optionName2ParentName, indentLevel + 1);
}
